import { LLM_USAGE_FEATURES } from "@nexus/contracts";
import type { TFunction } from "i18next";

/**
 * Display copy for the recorded feature values. The values themselves are
 * stable identifiers — renaming `persona-avatar` would split its history in
 * `llm_usage` — so the wording an operator reads is kept here, apart from the
 * thing the database stores.
 */
const FEATURE_LABELS: Record<string, { key: string; fallback: string }> = {
  [LLM_USAGE_FEATURES.ScriptGeneration]: {
    key: "admin.llmUsage.features.scriptGeneration",
    fallback: "Script generation",
  },
  [LLM_USAGE_FEATURES.ScriptEdit]: {
    key: "admin.llmUsage.features.scriptEdit",
    fallback: "Script edit",
  },
  [LLM_USAGE_FEATURES.TopicRecommendations]: {
    key: "admin.llmUsage.features.topicRecommendations",
    fallback: "Topic recommendations",
  },
  [LLM_USAGE_FEATURES.SetGeneration]: {
    key: "admin.llmUsage.features.setGeneration",
    fallback: "Set generation",
  },
  [LLM_USAGE_FEATURES.StoryboardBeats]: {
    key: "admin.llmUsage.features.storyboardBeats",
    fallback: "Storyboard beats",
  },
  [LLM_USAGE_FEATURES.StoryboardImage]: {
    key: "admin.llmUsage.features.storyboardImage",
    fallback: "Storyboard image",
  },
  [LLM_USAGE_FEATURES.ScriptReview]: {
    key: "admin.llmUsage.features.scriptReview",
    fallback: "Script review",
  },
  [LLM_USAGE_FEATURES.ScriptMatch]: {
    key: "admin.llmUsage.features.scriptMatch",
    fallback: "Script match",
  },
  [LLM_USAGE_FEATURES.ScriptExtraction]: {
    key: "admin.llmUsage.features.scriptExtraction",
    fallback: "Script extraction",
  },
  [LLM_USAGE_FEATURES.RejectionSuggestion]: {
    key: "admin.llmUsage.features.rejectionSuggestion",
    fallback: "Rejection suggestion",
  },
  [LLM_USAGE_FEATURES.PersonaAvatar]: {
    key: "admin.llmUsage.features.personaAvatar",
    fallback: "Persona avatar",
  },
  [LLM_USAGE_FEATURES.PersonaScriptGeneration]: {
    key: "admin.llmUsage.features.personaScriptGeneration",
    fallback: "Persona script generation",
  },
  [LLM_USAGE_FEATURES.PersonaScriptImport]: {
    key: "admin.llmUsage.features.personaScriptImport",
    fallback: "Persona script import",
  },
  [LLM_USAGE_FEATURES.PersonaBriefGeneration]: {
    key: "admin.llmUsage.features.personaBriefGeneration",
    fallback: "Persona brief generation",
  },
  [LLM_USAGE_FEATURES.PersonaBriefEdit]: {
    key: "admin.llmUsage.features.personaBriefEdit",
    fallback: "Persona brief edit",
  },
  [LLM_USAGE_FEATURES.ChangelogNotes]: {
    key: "admin.llmUsage.features.changelogNotes",
    fallback: "Changelog notes",
  },
  [LLM_USAGE_FEATURES.ChangelogDigest]: {
    key: "admin.llmUsage.features.changelogDigest",
    fallback: "Changelog digest",
  },
  [LLM_USAGE_FEATURES.PromptCatalog]: {
    key: "admin.llmUsage.features.promptCatalog",
    fallback: "Prompt catalog",
  },
};

/**
 * Anything outside the catalog renders as its raw value rather than as blank or
 * as a guess: a service shipping a new feature label reaches this table before
 * the copy for it does, and an unfamiliar `script-remix` is still readable.
 */
export function translateLlmUsageFeature(t: TFunction, feature: string): string {
  const label = FEATURE_LABELS[feature];
  return label ? t(label.key, label.fallback) : feature;
}
